export const GROUNDED_WORK_KIND = 'imai.grounded-work.v1' as const;

const MAX_FRONTMATTER_ENTRIES = 24;
const MAX_SECTIONS = 48;
const MAX_TITLE_LENGTH = 120;

export type GroundedWorkSectionKind =
  | 'summary'
  | 'direction'
  | 'evidence'
  | 'assumptions'
  | 'sources'
  | 'generation'
  | 'history'
  | 'other';

export type GroundedWorkSection = {
  anchor: string;
  body: string;
  depth: number;
  heading: string;
  itemId?: string;
  kind: GroundedWorkSectionKind;
};

export type GroundedWorkDocument = {
  body: string;
  frontmatter: Record<string, string>;
  kind: typeof GROUNDED_WORK_KIND;
  researchAsOf?: string;
  sections: GroundedWorkSection[];
  status?: string;
  title: string;
  type?: string;
};

type FlatMarkdownFrontmatter = {
  body: string;
  entries: Record<string, string>;
};

function clampText(value: string, length = MAX_TITLE_LENGTH): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, length);
}

function parseFlatFrontmatter(markdown: string): FlatMarkdownFrontmatter {
  const normalized = markdown.replace(/^\uFEFF/, '');
  if (!normalized.startsWith('---\n') && !normalized.startsWith('---\r\n')) {
    return { body: markdown, entries: {} };
  }
  const match = normalized.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/);
  if (!match) return { body: markdown, entries: {} };

  const entries: Record<string, string> = {};
  for (const rawLine of (match[1] ?? '')
    .split(/\r?\n/)
    .slice(0, MAX_FRONTMATTER_ENTRIES)) {
    const line = rawLine.trim();
    const separator = line.indexOf(':');
    if (!line || line.startsWith('#') || separator <= 0) continue;
    const key = line.slice(0, separator).trim();
    const value = line
      .slice(separator + 1)
      .trim()
      .replace(/^['"]|['"]$/g, '');
    if (key && value) entries[key] = value;
  }
  return { body: normalized.slice(match[0].length), entries };
}

function markdownAnchor(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/\{#[^}]+\}\s*$/, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

function explicitAnchor(heading: string): string | null {
  return heading.match(/\{#([a-z0-9][a-z0-9-_]{0,79})\}\s*$/i)?.[1] ?? null;
}

function cleanHeading(heading: string): string {
  return clampText(heading.replace(/\s*\{#[^}]+\}\s*$/, ''));
}

function sectionKind(heading: string): GroundedWorkSectionKind {
  const normalized = heading.toLowerCase();
  if (/^(direction|concept|option|variation)\s*[:—-]/.test(normalized)) {
    return 'direction';
  }
  if (
    /conclusion|recommendation|decision|summary|purpose|scope/.test(normalized)
  ) {
    return 'summary';
  }
  if (/evidence|research|finding|method|basis/.test(normalized)) {
    return 'evidence';
  }
  if (
    /assumption|limitation|constraint|unknown|open question|validation|risk/.test(
      normalized
    )
  ) {
    return 'assumptions';
  }
  if (/source|reference|citation/.test(normalized)) return 'sources';
  if (/generation detail|recipe|prompt|attempt/.test(normalized)) {
    return 'generation';
  }
  if (/change history|revision|history/.test(normalized)) return 'history';
  return 'other';
}

function parseSections(body: string): GroundedWorkSection[] {
  const matches = [...body.matchAll(/^(#{1,6})\s+(.+?)\s*$/gm)].slice(
    0,
    MAX_SECTIONS + 1
  );
  return matches.slice(0, MAX_SECTIONS).map((match, index) => {
    const rawHeading = match[2] ?? '';
    const heading = cleanHeading(rawHeading);
    const anchor = explicitAnchor(rawHeading) ?? markdownAnchor(heading);
    const bodyStart = (match.index ?? 0) + match[0].length;
    const bodyEnd = matches[index + 1]?.index ?? body.length;
    const kind = sectionKind(heading);
    return {
      anchor,
      body: body.slice(bodyStart, bodyEnd).trim(),
      depth: match[1]?.length ?? 2,
      heading,
      kind,
      ...(kind === 'direction' ? { itemId: anchor } : {}),
    };
  });
}

function markdownTitle(body: string): string | null {
  const match = body.match(/^#\s+(.+?)\s*$/m);
  return match?.[1] ? cleanHeading(match[1]) : null;
}

function filenameTitle(path?: string | null): string {
  const filename = path?.split('/').filter(Boolean).pop() ?? '';
  return clampText(
    filename
      .replace(/\.mdx?$/i, '')
      .replace(/[-_]+/g, ' ')
      .trim() || 'Document'
  );
}

export function parseGroundedWorkDocument(
  markdown: string,
  options: { path?: string | null } = {}
): GroundedWorkDocument | null {
  const parsed = parseFlatFrontmatter(markdown);
  if (parsed.entries.kind !== GROUNDED_WORK_KIND) return null;

  const title = clampText(
    parsed.entries.title ||
      markdownTitle(parsed.body) ||
      filenameTitle(options.path)
  );
  return {
    body: parsed.body.trim(),
    frontmatter: parsed.entries,
    kind: GROUNDED_WORK_KIND,
    sections: parseSections(parsed.body),
    title,
    ...(parsed.entries.type
      ? { type: clampText(parsed.entries.type, 48) }
      : {}),
    ...(parsed.entries.status
      ? { status: clampText(parsed.entries.status, 32).toLowerCase() }
      : {}),
    ...(parsed.entries.researchAsOf
      ? { researchAsOf: clampText(parsed.entries.researchAsOf, 32) }
      : {}),
  };
}

export function groundedWorkSections(
  document: GroundedWorkDocument,
  kind: GroundedWorkSectionKind
): GroundedWorkSection[] {
  return document.sections.filter((section) => section.kind === kind);
}

export function groundedWorkTypeLabel(type?: string): string | null {
  if (!type) return null;
  return clampText(type.replace(/[-_]+/g, ' '), 48);
}
